import React, { useEffect, useRef, useState } from "react";
import { Search, Loader2, MapPin, X } from "lucide-react";

/**
 * AddressAutocomplete — Swiggy/Zomato-style address search using OpenStreetMap Nominatim.
 *
 * Props:
 *  - onPick({ line1, line2, city, state, pincode, lat, lng, displayName }) — fires on selection.
 *  - defaultValue: string — initial search text.
 *  - placeholder: string
 *
 * Notes:
 *  - Respects Nominatim etiquette: debounced (400ms), sends a descriptive User-Agent via Referer.
 *  - `countrycodes=in` biases results to India.
 */
export function AddressAutocomplete({ onPick, defaultValue = "", placeholder = "Search area, building, street…" }) {
  const [query, setQuery] = useState(defaultValue);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const abortRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    // Close when clicking outside
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
    const q = query.trim();
    if (q.length < 3) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const ctl = new AbortController();
        abortRef.current = ctl;
        const url = new URL("https://nominatim.openstreetmap.org/search");
        url.searchParams.set("q", q);
        url.searchParams.set("format", "json");
        url.searchParams.set("addressdetails", "1");
        url.searchParams.set("countrycodes", "in");
        url.searchParams.set("limit", "6");
        const res = await fetch(url.toString(), {
          headers: { "Accept-Language": "en" },
          signal: ctl.signal,
        });
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch (e) {
        if (e.name !== "AbortError") setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [query]);

  const pick = (r) => {
    const a = r.address || {};
    // Compose a friendly line1 from the most specific pieces
    const line1Parts = [a.house_number, a.road || a.pedestrian || a.residential || a.neighbourhood || a.suburb].filter(Boolean);
    const line1 = line1Parts.join(", ") || r.display_name.split(",").slice(0, 2).join(", ");
    const line2 = [a.suburb, a.neighbourhood].filter(Boolean).filter((x, i, arr) => arr.indexOf(x) === i).join(", ");
    const city = a.city || a.town || a.village || a.municipality || a.county || "";
    const state = a.state || "";
    const pincode = a.postcode || "";
    const lat = r.lat ? parseFloat(r.lat) : null;
    const lng = r.lon ? parseFloat(r.lon) : null;
    onPick && onPick({ line1, line2, city, state, pincode, lat, lng, displayName: r.display_name });
    setQuery(r.display_name);
    setOpen(false);
  };

  const clear = () => {
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative" data-testid="address-autocomplete">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-hyd-gold" />
        <input
          data-testid="address-autocomplete-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="w-full h-11 pl-9 pr-9 rounded-2xl bg-hyd-bg border-2 border-hyd-gold/60 text-hyd-text placeholder:text-hyd-text3 focus:outline-none focus:border-hyd-gold shadow-glow-gold text-sm"
        />
        {loading ? (
          <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-hyd-gold animate-spin" />
        ) : query ? (
          <button type="button" onClick={clear} className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded-full text-hyd-text2 hover:text-hyd-red">
            <X className="w-4 h-4" />
          </button>
        ) : null}
      </div>
      {open && (results.length > 0 || (!loading && query.trim().length >= 3)) && (
        <div
          data-testid="address-autocomplete-results"
          className="absolute z-50 top-full left-0 right-0 mt-1 bg-hyd-panel border border-hyd-border rounded-2xl shadow-card-dark max-h-72 overflow-auto"
        >
          {results.length === 0 ? (
            <div className="p-3 text-xs text-hyd-text3">No matches. Try a broader search.</div>
          ) : (
            results.map((r) => (
              <button
                key={r.place_id}
                type="button"
                data-testid={`address-autocomplete-option-${r.place_id}`}
                onClick={() => pick(r)}
                className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-hyd-bg border-b border-hyd-border/50 last:border-b-0"
              >
                <MapPin className="w-4 h-4 text-hyd-gold shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-hyd-text line-clamp-1">
                    {r.display_name.split(",")[0]}
                  </div>
                  <div className="text-[11px] text-hyd-text2 line-clamp-2">{r.display_name}</div>
                </div>
              </button>
            ))
          )}
          <div className="px-3 py-1.5 text-[10px] text-hyd-text3 border-t border-hyd-border/50">
            Results via OpenStreetMap
          </div>
        </div>
      )}
    </div>
  );
}
